import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { User } from '../contexts/auth.types';
import { loadUserProfile as defaultLoadUserProfile } from './useAuthSynchronization/loadUserProfile';

export function useRoleManagement(
  user: User | null,
  setUser: Dispatch<SetStateAction<User | null>>,
  loadUserProfileFn: (userId: string, userEmail?: string | null) => Promise<User | null> = defaultLoadUserProfile
) {
  const queryClient = useQueryClient();

  const hasPermission = useCallback((permission: string): boolean => {
    return user?.permissions.includes(permission) || false;
  }, [user]);

  const hasAnyPermission = useCallback((permissions: string[]): boolean => {
    if (!user?.permissions) return false;
    return permissions.some(permission => user.permissions.includes(permission));
  }, [user]);

  const isDualRole = useCallback((): boolean => {
    return user?.account_mode === 'dual';
  }, [user]);

  const switchRole = useCallback(async (newRole: 'customer' | 'trade_company') => {
    if (!user) {
      throw new Error('No user logged in');
    }

    if (user.account_mode !== 'dual') {
      throw new Error('User is not a dual-role account');
    }

    if (!user.customer_id || !user.trade_company_id) {
      throw new Error('User does not have both customer and trade company accounts');
    }

    try {
      // Update last_active_role in database
      const { error } = await supabase
        .from('profiles')
        .update({ last_active_role: newRole })
        .eq('id', user.id);

      if (error) {
        console.error('❌ Error updating last_active_role:', error);
        throw error;
      }

      // Reload user profile in memory when switching roles
      const reloadedUser = await loadUserProfileFn(user.id, user.email);

      if (reloadedUser) {
        setUser(reloadedUser);
      } else {
        const newCompanyId = newRole === 'customer' ? user.customer_id : user.trade_company_id;
        setUser({
          ...user,
          active_role: newRole,
          company_id: newCompanyId,
        });
      }

      // Invalidate queries so opposite-role cached data is purged
      await queryClient.invalidateQueries();
    } catch (error) {
      console.error('💥 Error switching role:', error);
      throw error;
    }
  }, [user, setUser, queryClient, loadUserProfileFn]);

  return { hasPermission, hasAnyPermission, isDualRole, switchRole };
}
